import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { MailCheck } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import { useResendVerification } from "../hooks/useResendVerification";
import { useResendCooldown } from "../hooks/useResendCooldown";
import { getSignupAttribution } from "../hooks/useLandingAnalytics";
import { setPendingVerificationEmail } from "../utils/pendingVerification";
import type { ApiError } from "../api/http";
import { AuthLayout } from "../components/auth/AuthLayout";
import { PasswordInput } from "../components/auth/PasswordInput";
import {
  authFieldError,
  authFormError,
  authInput,
  authLabel,
  authLink,
  authMuted,
  authPrimaryBtn,
  authSecondaryBtn,
} from "../components/auth/authStyles";

export function RegisterPage() {
  const { register } = useAuth();
  const { addToast } = useToast();
  const { t, i18n } = useTranslation();
  const resend = useResendVerification();
  const [searchParams] = useSearchParams();
  // Landing-page CTAs arrive as /register?src=landing&email=… — prefill the
  // email and tag the signup for the platform funnel.
  const fromLanding = searchParams.get("src") === "landing";

  const [form, setForm] = useState({
    email: searchParams.get("email") ?? "",
    first_name: "",
    last_name: "",
    password: "",
    password_confirm: "",
  });
  const [tosAccepted, setTosAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [emailSendFailed, setEmailSendFailed] = useState(false);
  const [resendOk, setResendOk] = useState(false);
  const { cooldown, start: startCooldown } = useResendCooldown("register");

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.email) newErrors.email = t("auth.register.validation.emailRequired");
    if (!form.first_name) newErrors.first_name = t("auth.register.validation.firstNameRequired");
    if (!form.last_name) newErrors.last_name = t("auth.register.validation.lastNameRequired");
    if (form.password.length < 8) newErrors.password = t("auth.register.validation.passwordTooShort");
    if (form.password !== form.password_confirm) {
      newErrors.password_confirm = t("auth.register.validation.passwordsDoNotMatch");
    }
    if (!tosAccepted) {
      newErrors.tos_accepted = t("legal.acceptance.checkboxRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      // The verification email is sent in the language the form was
      // submitted in.
      const language = (i18n.language ?? "en").split("-")[0] === "es" ? "es" : "en";
      // Acquisition attribution (visitor_id + UTM/referrer) — null when the
      // visitor opted out of analytics or nothing was captured.
      const attribution = getSignupAttribution();
      const response = await register({
        ...form,
        tos_accepted: tosAccepted,
        language,
        ...(fromLanding ? { source: "landing" } : {}),
        ...(attribution ? { attribution } : {}),
      });
      setSuccess(true);
      // Pre-fills the resend form on VerifyEmailPage if the link never arrives.
      setPendingVerificationEmail(form.email);
      setEmailSendFailed(response.email_sent === false);
      addToast(t("auth.register.successToast"), "success");
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.errors && typeof apiError.errors === "object") {
        const fieldErrors: Record<string, string> = {};
        for (const [key, value] of Object.entries(apiError.errors)) {
          if (Array.isArray(value)) {
            // The duplicate-email rejection is the one field error worth
            // localizing (backend messages are English-only).
            fieldErrors[key] =
              key === "email" && value[0].includes("already exists")
                ? t("auth.register.validation.emailExists")
                : value[0];
          }
        }
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
        } else {
          setErrors({ form: apiError.detail || t("auth.register.genericError") });
        }
      } else {
        setErrors({ form: apiError.detail || t("auth.register.genericError") });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleResend = async () => {
    if (cooldown > 0 || resend.isPending || !form.email) return;
    try {
      await resend.mutateAsync({ email: form.email });
    } catch {
      // Endpoint is enumeration-safe; the rate-limit response is also opaque
      // to the user — surface the same "sent" affordance either way.
    }
    setResendOk(true);
    startCooldown();
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-emerald-600 dark:bg-emerald-500/15 dark:ring-emerald-500/30 dark:text-emerald-300">
            <MailCheck size={22} />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{t("auth.register.successTitle")}</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            <Trans
              i18nKey="auth.register.successWhy"
              components={{ strong: <strong /> }}
            />
          </p>
          <p className={`mt-2 text-sm ${authMuted}`}>
            <strong className="text-slate-900 dark:text-white">{form.email}</strong>
          </p>
          <p className={`mt-3 text-sm ${authMuted}`}>{t("auth.register.successWhen")}</p>
          <p className={`mt-1 text-xs ${authMuted}`}>{t("auth.register.successExpiry")}</p>
          {emailSendFailed && (
            <div className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-left text-sm text-amber-800 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30">
              {t("auth.register.emailSendFailed")}
            </div>
          )}

          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0 || resend.isPending}
            className={`mt-5 ${authSecondaryBtn}`}
          >
            {resend.isPending
              ? t("auth.register.successResending")
              : cooldown > 0
                ? t("auth.verify.resendCooldown", { seconds: cooldown })
                : t("auth.register.successResend")}
          </button>
          {resendOk && cooldown > 0 ? (
            <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-300">
              {t("auth.register.successResendSent")}
            </p>
          ) : null}

          <Link to="/login" className={`mt-6 inline-block text-sm ${authLink}`}>
            {t("auth.register.successBackLink")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.register.subtitle")} width="md">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {errors.form && <div className={authFormError}>{errors.form}</div>}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="first_name" className={authLabel}>
              {t("auth.register.firstName")}
            </label>
            <input
              id="first_name"
              type="text"
              required
              autoComplete="given-name"
              value={form.first_name}
              onChange={(e) => updateField("first_name", e.target.value)}
              className={authInput}
            />
            {errors.first_name && <p className={authFieldError}>{errors.first_name}</p>}
          </div>
          <div>
            <label htmlFor="last_name" className={authLabel}>
              {t("auth.register.lastName")}
            </label>
            <input
              id="last_name"
              type="text"
              required
              autoComplete="family-name"
              value={form.last_name}
              onChange={(e) => updateField("last_name", e.target.value)}
              className={authInput}
            />
            {errors.last_name && <p className={authFieldError}>{errors.last_name}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="email" className={authLabel}>
            {t("auth.register.email")}
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
            className={authInput}
            placeholder={t("auth.register.emailPlaceholder")}
          />
          {errors.email && <p className={authFieldError}>{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="password" className={authLabel}>
            {t("auth.register.password")}
          </label>
          <PasswordInput
            id="password"
            required
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => updateField("password", e.target.value)}
            placeholder={t("auth.register.passwordPlaceholder")}
          />
          {errors.password && <p className={authFieldError}>{errors.password}</p>}
        </div>

        <div>
          <label htmlFor="password_confirm" className={authLabel}>
            {t("auth.register.confirmPassword")}
          </label>
          <PasswordInput
            id="password_confirm"
            required
            autoComplete="new-password"
            value={form.password_confirm}
            onChange={(e) => updateField("password_confirm", e.target.value)}
            placeholder={t("auth.register.confirmPlaceholder")}
          />
          {errors.password_confirm && (
            <p className={authFieldError}>{errors.password_confirm}</p>
          )}
        </div>

        <div className="pt-1">
          <label className="flex cursor-pointer select-none items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input
              id="tos_accepted"
              type="checkbox"
              checked={tosAccepted}
              onChange={(e) => {
                setTosAccepted(e.target.checked);
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.tos_accepted;
                  return next;
                });
              }}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500 dark:border-white/20 dark:bg-white/5 dark:text-blue-500"
            />
            <span className="leading-snug">
              <Trans
                i18nKey="legal.acceptance.checkboxLabel"
                components={{
                  terms: (
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={authLink}
                    />
                  ),
                  privacy: (
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={authLink}
                    />
                  ),
                }}
              />
            </span>
          </label>
          {errors.tos_accepted && <p className={authFieldError}>{errors.tos_accepted}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !tosAccepted}
          className={authPrimaryBtn}
        >
          {isSubmitting ? t("auth.register.submitting") : t("auth.register.submit")}
        </button>
      </form>

      <div className={`mt-5 border-t border-slate-200 pt-4 text-center text-sm dark:border-white/5 ${authMuted}`}>
        {t("auth.register.loginPrompt")}{" "}
        <Link to="/login" className={authLink}>
          {t("auth.register.loginLink")}
        </Link>
        {/* Two doors: owners sign up here; invited employees join via their
            invitation link (JoinBusinessPage at /invite), never this form. */}
        <p className="mt-2 text-xs">{t("auth.register.inviteHint")}</p>
      </div>
    </AuthLayout>
  );
}
